import { useCallback, useRef, useState } from 'react';
import clsx from 'clsx';
import { PlateElement, useEditorRef, useEditorSelector, useReadOnly } from 'platejs/react';
import {
    TableProvider,
    useTableElement,
    useTableMergeState,
    useTableCellElement,
    useTableCellElementResizable,
    useTableColSizes,
    useTableValue,
} from '@platejs/table/react';
import { ResizeHandle } from '@platejs/resizable';
import { isSelectingCell, splitTableCell } from '@platejs/table';
import styles from './tableElements.module.scss';
import { CellContextMenu, type CellContextMenuEntry } from '../CellContextMenu/CellContextMenu';
import { TableHoverProvider, useTableHover } from './tableHoverContext';
import { TableHandlesOverlay } from './TableHandlesOverlay';

// Plate v53's table plugins ship no built-in `render: { as }` — they expect
// custom React components. We attach these via `.withComponent()` in
// usePlateEditorLogic so the table renders as real <table>/<tr>/<td>/<th>.
//
// CRITICAL: every table is wrapped in <TableProvider>. The hooks below
// (useTableElement, useTableMergeState, useTableCellElement,
// useTableCellElementResizable, useTableColSizes) all read from a jotai
// store scoped per-table by that Provider. Without it Plate falls back to a
// shared global store and (a) logs "jotai store 'table' outside of a matching
// provider" on every render, (b) re-renders every table on any store change,
// which clobbers DOM-level drag previews and breaks column resize.

export function TableElement(props: any) {
    return (
        <TableProvider>
            <TableHoverProvider>
                <TableElementInner {...props} />
            </TableHoverProvider>
        </TableProvider>
    );
}

function TableElementInner(props: any) {
    const readOnly = useReadOnly();
    const selecting = useEditorSelector((ed: any) => (isSelectingCell as any)(ed), []);
    // Library hook: collapses stale multi-cell selection when the user clicks
    // the table border (spread via tableProps onto the <table>). Provides
    // `marginLeft` for nested-list offset (we read but don't apply it yet).
    const { props: tableProps } = (useTableElement as any)();
    // Effective column sizes, including any drag-time override the resize
    // handle is currently writing to the table store. Rendering <col> widths
    // from this means the drag preview updates live without us touching the
    // DOM ourselves.
    const colSizes: number[] | undefined = (useTableColSizes as any)();
    const { hideDeferred } = useTableHover();
    const tableRef = useRef<HTMLTableElement | null>(null);

    // No wrapper around the <table>. Handles are portalled to document.body
    // by TableHandlesOverlay so they don't need a positioned ancestor and
    // don't reserve any DOM space around the table. An earlier wrapper-with-
    // padding approach worked at the editor root but pushed nested tables
    // (e.g. inside a column-layout column) past their parent's left edge
    // and on top of the column separator.
    return (
        <>
            <PlateElement
                as="table"
                {...props}
                {...tableProps}
                ref={tableRef as any}
                className={clsx(props.className, selecting && styles.tableSelecting)}
                style={{ ...(props.style ?? {}), borderCollapse: 'collapse' as const }}
                onMouseLeave={readOnly ? undefined : hideDeferred}
            >
                {colSizes && colSizes.length > 0 ? (
                    <colgroup contentEditable={false}>
                        {colSizes.map((w, i) =>
                            typeof w === 'number' && w > 0 ? <col key={i} style={{ width: `${w}px` }} /> : <col key={i} />,
                        )}
                    </colgroup>
                ) : null}
                {/* <tbody> wraps the rows so React's validateDOMNesting doesn't warn
                    (`<tr> cannot appear as a child of <table>`). Transparent to slate. */}
                <tbody>{props.children}</tbody>
            </PlateElement>
            {!readOnly && <TableHandlesOverlay tableElement={props.element} tableRef={tableRef} />}
        </>
    );
}

export function TableRowElement(props: any) {
    const editor = useEditorRef();
    // Back-compat read: the library transform `setTableRowSize` writes `size`,
    // older docs persisted `height`. Render either so legacy rows still load
    // at their saved height after the resize path switches to the library API.
    const persistedRowSize: number | undefined = props.element?.size ?? props.element?.height;

    // PERF: row resize was visibly laggier than column resize because the
    // library's default plumbing applies the row-size override as `minHeight`
    // on every cell via useTableCellElement() — N cells × ~60 ticks/sec of
    // drag = N reflows per pointer-move. Column resize doesn't have this
    // problem because its override flows into a single <colgroup> at the
    // table level (1 reflow per tick, browser reflows the whole column for
    // free under `table-layout: fixed`).
    //
    // Fix: read the row-size override map from the table store ONCE and
    // apply `height` at the <tr> level. Cells inside stretch to fill via
    // standard table-cell vertical layout (vertical-align: top from our
    // editor CSS). One reflow per row per tick — drag is now smooth.
    //
    // Trade-off: useTableValue('rowSizeOverrides') subscribes to the whole
    // map, so every TableRowElement re-renders on any row drag. That's N
    // TR renders vs the previous N*M cell renders — still a big win.
    const overrideMap: any = (useTableValue as any)('rowSizeOverrides');
    let rowIndex: number | undefined;
    try {
        const path = (editor as any)?.api?.findPath?.(props.element);
        if (Array.isArray(path) && path.length > 0) rowIndex = path[path.length - 1];
    } catch {
        /* defensive */
    }
    const overrideHeight: number | undefined =
        rowIndex != null && overrideMap && typeof overrideMap.get === 'function' ? overrideMap.get(rowIndex) : undefined;
    const effectiveHeight = typeof overrideHeight === 'number' ? overrideHeight : persistedRowSize;

    return (
        <PlateElement
            as="tr"
            {...props}
            style={{ ...(props.style ?? {}), ...(effectiveHeight ? { height: `${effectiveHeight}px` } : {}) }}
        />
    );
}

function useCellMenu(cellElement: any, isInSelection: boolean) {
    const editor = useEditorRef();
    const [menuPos, setMenuPos] = useState<{ x: number; y: number } | null>(null);
    // Library hook: derives canMerge / canSplit from the current selection +
    // read-only state + plugin options. Now correct because we're inside
    // <TableProvider> — earlier it was reading a global fallback store and
    // produced incorrect values (merge wouldn't fire).
    const { canSplit } = (useTableMergeState as any)();

    const open = (e: React.MouseEvent) => {
        e.preventDefault();
        // Right-click INSIDE an existing multi-cell selection: preserve the
        // selection so the menu's operations target every selected cell.
        // Right-click OUTSIDE (single cell, or different cell): re-anchor the
        // caret to this cell and clear any stale selection so transforms
        // target the right row + column.
        if (!isInSelection) {
            try {
                const ed: any = editor;
                const path = ed?.api?.findPath?.(cellElement);
                if (path) {
                    const start = ed?.api?.start?.(path);
                    if (start) ed.tf.select(start);
                    ed?.tf?.focus?.();
                }
                const tablePlugin = ed?.getPlugin?.({ key: 'table' });
                tablePlugin?.tf?.setOption?.('_selectedCellIds', undefined);
            } catch {
                /* defensive: editor may not be ready */
            }
        }
        setMenuPos({ x: e.clientX, y: e.clientY });
    };

    const close = useCallback(() => setMenuPos(null), []);

    // Programmatic row/column selection. Strategy: set a Slate range from
    // the start of the first cell in the row/column to the end of the last
    // cell. Plate's table plugin reads this range, computes its bounding
    // rectangle via `getTableGridByRange`, and highlights every cell inside
    // — which for a single-row range is the row, and for a single-column
    // range (top-left → bottom-left of the column) is the column.
    //
    // We resolve the cell's grid coordinates by walking up from the cell
    // path (slate path = [tableIdx, rowIdx, colIdx]). Defensive guards
    // protect against weird paths (e.g. cell removed before menu fires).
    const selectRowOrColumn = (mode: 'row' | 'column') => {
        try {
            const ed: any = editor;
            const cellPath: number[] | undefined = ed?.api?.findPath?.(cellElement);
            if (!cellPath || cellPath.length < 2) return;
            const colIdx = cellPath[cellPath.length - 1];
            const rowIdx = cellPath[cellPath.length - 2];
            const tablePath = cellPath.slice(0, -2);
            const tableNode: any = ed?.api?.node?.(tablePath)?.[0];
            if (!tableNode?.children?.length) return;
            let startPath: number[];
            let endPath: number[];
            if (mode === 'row') {
                const row = tableNode.children[rowIdx];
                if (!row?.children?.length) return;
                startPath = [...tablePath, rowIdx, 0];
                endPath = [...tablePath, rowIdx, row.children.length - 1];
            } else {
                startPath = [...tablePath, 0, colIdx];
                endPath = [...tablePath, tableNode.children.length - 1, colIdx];
            }
            const start = ed?.api?.start?.(startPath);
            const end = ed?.api?.end?.(endPath);
            if (start && end) {
                ed?.tf?.select?.({ anchor: start, focus: end });
                ed?.tf?.focus?.();
            }
        } catch {
            /* defensive — table may have been mutated under us */
        }
    };

    const setVerticalAlign = (value: 'top' | 'middle' | 'bottom') => {
        try {
            const ed: any = editor;
            const tableApi = ed?.api?.table ?? ed?.getApi?.({ key: 'table' })?.table;
            const selected = tableApi?.getSelectedCells?.();
            if (Array.isArray(selected) && selected.length > 1) {
                // Multi-cell — set on every selected cell node.
                ed?.tf?.setNodes?.({ verticalAlign: value }, { match: (n: any) => selected.includes(n) });
                return;
            }
            const path = ed?.api?.findPath?.(cellElement);
            if (path) ed?.tf?.setNodes?.({ verticalAlign: value }, { at: path });
        } catch {
            /* defensive */
        }
    };

    // `verticalAlign` is a LOCAL extension on td/th nodes — Plate's renderer
    // doesn't know about it; TableCellElement reads it and applies an inline
    // style. If HTML export is added later, register a custom td/th
    // serializer or this property silently drops.
    const entries: CellContextMenuEntry[] = [
        { label: 'Select row', onClick: () => selectRowOrColumn('row') },
        { label: 'Select column', onClick: () => selectRowOrColumn('column') },
        { divider: true },
        {
            label: 'Insert row above',
            onClick: () => (editor as any).tf?.insert?.tableRow?.({ before: true }),
        },
        {
            label: 'Insert row below',
            onClick: () => (editor as any).tf?.insert?.tableRow?.(),
        },
        { divider: true },
        {
            label: 'Insert column left',
            onClick: () => (editor as any).tf?.insert?.tableColumn?.({ before: true }),
        },
        {
            label: 'Insert column right',
            onClick: () => (editor as any).tf?.insert?.tableColumn?.(),
        },
        { divider: true },
        // Split is kept so users can manually break apart any merged cell that
        // sneaks in via paste; Merge is intentionally absent — the migration
        // unmerges every cell on load.
        ...(canSplit
            ? ([{ label: 'Split cell', onClick: () => (splitTableCell as any)(editor) }, { divider: true }] as CellContextMenuEntry[])
            : []),
        { label: 'Align top', onClick: () => setVerticalAlign('top') },
        { label: 'Align middle', onClick: () => setVerticalAlign('middle') },
        { label: 'Align bottom', onClick: () => setVerticalAlign('bottom') },
        { divider: true },
        {
            label: 'Delete row',
            danger: true,
            onClick: () => (editor as any).tf?.remove?.tableRow?.(),
        },
        {
            label: 'Delete column',
            danger: true,
            onClick: () => (editor as any).tf?.remove?.tableColumn?.(),
        },
        {
            label: 'Delete table',
            danger: true,
            onClick: () => (editor as any).tf?.remove?.table?.(),
        },
    ];

    return { menuPos, open, close, entries };
}

// @platejs/resizable's <ResizeHandle> is a low-level primitive: it does NOT
// preventDefault on mousedown, does NOT lock document.body.style.cursor, and
// does NOT set body.userSelect = 'none' during the drag. The old hand-rolled
// ColumnResizeHandle / RowResizeHandle did all three, which is what kept the
// drag clean. Without those locks:
//   - the browser starts native text selection from the click point and
//     extends it across the row/below rows as the pointer moves
//   - the cursor flickers between col-resize/row-resize and text whenever
//     the pointer leaves the 4-px handle strip
// Capture-phase mousedown fires before the library's bubble-phase handler,
// so we can restore the locks here without fighting Plate's resize state.
function makeResizeLockHandler(cursor: 'col-resize' | 'row-resize') {
    return (e: React.MouseEvent) => {
        e.preventDefault();
        const body = document.body;
        const prevCursor = body.style.cursor;
        const prevSelect = body.style.userSelect;
        body.style.cursor = cursor;
        body.style.userSelect = 'none';
        const release = () => {
            body.style.cursor = prevCursor;
            body.style.userSelect = prevSelect;
            window.removeEventListener('mouseup', release);
            window.removeEventListener('touchend', release);
            window.removeEventListener('touchcancel', release);
        };
        window.addEventListener('mouseup', release);
        window.addEventListener('touchend', release);
        window.addEventListener('touchcancel', release);
    };
}
const lockColResize = makeResizeLockHandler('col-resize');
const lockRowResize = makeResizeLockHandler('row-resize');

function Cell({ as, ...props }: any) {
    const readOnly = useReadOnly();
    // Single library hook — gives us colIndex / colSpan / rowIndex / selected /
    // borders / width / minHeight / isSelectingCell in one call. Replaces our
    // earlier hand-wired useCellIndices + useIsCellSelected pair.
    const cellState: any = (useTableCellElement as any)();
    const selected: boolean = Boolean(cellState?.selected);
    // INTENTIONAL: we do NOT apply cellState.minHeight as inline style on
    // the cell. The library plumbs the row-size override into every cell's
    // minHeight, which caused N reflows per drag tick (laggy row resize).
    // TableRowElement above now reads the override directly from the table
    // store and applies `height` at the <tr> level — one reflow per tick.
    // Cells inside stretch to fill the row via standard table layout, so
    // no visual difference, just much smoother dragging.
    // Library hook returns props for three resize handles (right / bottom /
    // left). Each handle's drag fires a store-level override (no DOM mutation
    // by us), commits via setTableColSize / setTableRowSize on release. Drag
    // previews now survive any Cell re-render because the override flows
    // through useTableColSizes → <col> widths reactively.
    const { rightProps, bottomProps, leftProps, hiddenLeft } = (useTableCellElementResizable as any)({
        colIndex: cellState?.colIndex ?? 0,
        colSpan: cellState?.colSpan ?? 1,
        rowIndex: cellState?.rowIndex ?? 0,
    });
    const { menuPos, open, close, entries } = useCellMenu(props.element, selected);
    const { show: showHover } = useTableHover();
    // Plate's @platejs/table `setCellBackground` writes the property as
    // `background` (not `backgroundColor`). Our single-cell paint path in
    // CellBackgroundMenu used to write `backgroundColor`, so we read EITHER
    // here. New cell fills should land under `background` going forward —
    // see the serializers (slateToPlate / plateToSlate) for the round-trip
    // mapping that converts to/from the legacy Slate `backgroundColor` key.
    const cellBg: string | undefined = props.element?.background ?? props.element?.backgroundColor;
    const verticalAlign: string | undefined = props.element?.verticalAlign;

    // Stop right-mousedown on a selected cell from moving the caret —
    // Slate's mousedown fires before contextmenu and would otherwise
    // clobber the multi-cell selection before the menu opens.
    const handleMouseDown = (e: React.MouseEvent) => {
        if (e.button === 2 && selected) e.preventDefault();
    };

    // Surface this cell's (rowIndex, colIndex) to the table-level hover
    // context so the floating row/column drag handles know where to anchor.
    // No-op on mouseleave — hide is handled at the wrapper level (with a
    // short delay) so the pointer can travel from cell border to handle
    // without flicker.
    const handleMouseEnter = () => {
        showHover(cellState?.rowIndex ?? 0, cellState?.colIndex ?? 0);
    };

    // Read-only short-circuit: history-preview / archived views shouldn't be
    // drag-resizable or right-clickable into destructive transforms. Skip the
    // resize handles AND the context-menu wiring entirely.
    const cellAttributes = readOnly
        ? props.attributes
        : { ...props.attributes, onContextMenu: open, onMouseDown: handleMouseDown, onMouseEnter: handleMouseEnter };

    return (
        <>
            <PlateElement
                as={as}
                {...props}
                attributes={cellAttributes}
                className={clsx(props.className, selected && styles.cellSelected)}
                style={{
                    ...(props.style ?? {}),
                    // ResizeHandles below are `position: absolute`. Without an
                    // unconditional positioning context on the cell, they
                    // resolve to the nearest positioned ancestor (which varies
                    // between selected/unselected — `.cellSelected` is the only
                    // other source of `position: relative`). Pin it here so the
                    // handles always anchor to the cell box.
                    position: 'relative',
                    border: '1px solid #d8d6d2',
                    padding: '10px 12px',
                    ...(cellBg ? { backgroundColor: cellBg } : {}),
                    ...(verticalAlign ? { verticalAlign } : {}),
                }}
            >
                {props.children}
                {!readOnly && (
                    <>
                        {/*
                         * `{...rightProps/bottomProps/leftProps}` from
                         * useTableCellElementResizable spreads the library's
                         * inline `style` (position, cursor, drag handlers AND
                         * an oversized grab zone — ~8–10 px). Inline style
                         * wins over our SCSS, so the visible handle (`.colHandle`
                         * etc.) is silently inflated and the resize cursor
                         * fires well inside the cell. Re-apply our intended
                         * 4 px touch strip + `-2 px` offset AFTER the spread
                         * to clip the zone back to the cell edge. Library
                         * handlers stay intact because we only override
                         * dimensions, not the event props.
                         */}
                        <ResizeHandle
                            {...rightProps}
                            className={styles.colHandle}
                            contentEditable={false}
                            // Hit strip is 10 px wide, biased INWARD (right: -0.5
                            // keeps the strip's right edge — and therefore the
                            // ::after visible line — pinned to the cell border).
                            // Previously width:1 made the cursor flicker as the
                            // pointer left a 1 px zone with the slightest move.
                            style={{ ...(rightProps?.style ?? {}), width: 10, right: -0.5 }}
                            onMouseDownCapture={lockColResize}
                        />
                        <ResizeHandle
                            {...bottomProps}
                            className={styles.rowHandle}
                            contentEditable={false}
                            // Same widening for row resize — height: 10 biased
                            // upward (bottom: -0.5 keeps the visible line on
                            // the cell's bottom border).
                            style={{ ...(bottomProps?.style ?? {}), height: 10, bottom: -0.5 }}
                            onMouseDownCapture={lockRowResize}
                        />
                        {!hiddenLeft && (
                            <ResizeHandle
                                {...leftProps}
                                className={styles.leftHandle}
                                contentEditable={false}
                                // Mirror of the right handle, biased into the
                                // first cell (left: -0.5 anchors the visible
                                // ::after line on the cell's left border).
                                style={{ ...(leftProps?.style ?? {}), width: 10, left: -0.5 }}
                                onMouseDownCapture={lockColResize}
                            />
                        )}
                    </>
                )}
            </PlateElement>
            {!readOnly && menuPos && <CellContextMenu x={menuPos.x} y={menuPos.y} entries={entries} onClose={close} />}
        </>
    );
}

export function TableCellElement(props: any) {
    return <Cell as="td" {...props} />;
}

export function TableCellHeaderElement(props: any) {
    return <Cell as="th" {...props} />;
}
